import React, { useState, useMemo, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import qahoy from "views_shared/app/javascript/scripts/qahoy";
import qapi from "views_shared/app/javascript/api";

const CompanyContext = React.createContext(null);

// page_config.display_style_preference (human-edited) and subtype (computer-generated) are mashed
// into a synthetic `displayType`, keeping the two independent. Extracted as one helper so the
// post-fetch derivation and the server-seeded value (used to render the loading skeleton with the
// correct rail decision — see CompanyContextProvider's initial `company` state) stay identical.
// Unknown/absent preference falls back to the subtype (or "default") rather than throwing, so a
// stray value degrades gracefully instead of erroring the whole page.
export function deriveDisplayType(displayStylePreference, subtype) {
  switch (displayStylePreference) {
    case "company":
      return "default";
    case "pr_firm":
      return "pr_firm";
    default: // "default" (or unset): use the computer-generated subtype
      return subtype || "default";
  }
}

function CompanyContextProvider(props) {
  const { children } = props;

  // ViewerContext seed: a small server-computed object ({ editable, isAuthenticated, role })
  // that replaces the old bare `companyEditable` boolean. The affordance layer reads
  // `viewerContext.editable`; later phases extend it with the paywall axes (plan_paid,
  // within_pitch_limits). Default to an empty object so a missing prop reads as non-editable.
  // companySeed: { subtype, displayStylePreference } passed from the server (ERB) so the provider
  // can derive `displayType` BEFORE the async fetch resolves. That lets the loading skeleton make
  // the same rail-collapse decision as the loaded page (no two-column → full-width reflow). The
  // fetch later replaces `company` wholesale (re-deriving displayType from the same inputs).
  // eslint-disable-next-line react/destructuring-assignment
  const { viewerContext = {}, companySeed = null } = props.value;

  // alphabetized
  const [aboutSectionHidden, setAboutSectionHidden] = useState(false);
  const [asyncError, setAsyncError] = useState(null);
  const [company, setCompany] = useState(() =>
    companySeed ? { displayType: deriveDisplayType(companySeed.displayStylePreference, companySeed.subtype) } : {},
  );
  const [companyId, setCompanyId] = useState(null);
  const [doTopLevelFetch, setDoTopLevelFetch] = useState(true);
  const [generalError, setGeneralError] = useState(null);
  const [hashtagsSectionHidden, setHashtagsSectionHidden] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(null);
  const [prAgenciesExist, setPrAgenciesExist] = useState(false);
  const [prAgenciesSectionHidden, setPrAgenciesSectionHidden] = useState(false);
  const [prProfilesExist, setPrProfilesExist] = useState(false);
  const [prProfilesSectionHidden, setPrProfilesSectionHidden] = useState(false);
  const [topLevelIsLoading, setTopLevelIsLoading] = useState(false);

  // The top-level company read, as one callable the provider also exposes. Setting doTopLevelFetch
  // only schedules the read (the effect below runs it), so a caller that must not act on the company
  // it already has until the fresh one has arrived — <ProfileImage> holds its controls disabled
  // until its onChange resolves, or a second upload would be aimed at the pre-save image — has to be
  // able to await the read itself. The flag keeps its existing meaning for every consumer that sets it.
  const reloadCompany = useCallback(async () => {
    if (!companyId) return;

    try {
      setTopLevelIsLoading(true);
      qahoy.track("Companies#companies.get");

      const response = await qapi.companies.get(companyId);

      if (response.data) {
        const companyData = response.data;
        if (companyData[0]) {
          const c = companyData[0];

          // Synthetic displayType from the human-edited preference + computer-generated subtype
          // (see deriveDisplayType above — same helper used for the server seed).
          c.displayType = deriveDisplayType(c.page_config?.display_style_preference, c.subtype);

          setCompany(c);
        }
      } else {
        throw new Error(JSON.stringify(response.error));
      }
    } catch (error) {
      setAsyncError(error);
    } finally {
      setTopLevelIsLoading(false);
      setDoTopLevelFetch(false);
    }
  }, [companyId]);

  // Pulls company info from the API whenever the companyId changes or doTopLevelFetch is set to true
  useEffect(() => {
    if (!doTopLevelFetch || !companyId) return;

    reloadCompany();
  }, [companyId, doTopLevelFetch, reloadCompany]);

  // Keeps the hashtags section hidden boolean updated
  useEffect(() => {
    setHashtagsSectionHidden(!(company.hashtags?.length > 0));
  }, [company, setHashtagsSectionHidden]);

  // Keeps the about section hidden boolean updated
  useEffect(() => {
    setAboutSectionHidden(!company.description);
  }, [company, setAboutSectionHidden]);

  // Keeps the PR users section hidden boolean updated
  useEffect(() => {
    // Only show this in the "Add a section" list if there actually are PR users returned from
    // the API and the section has been set explicitly to not show. Otherwise, it's just hidden
    // and not shown in the "Add a section" at all (because adding it wouldn't do any good without records.)
    setPrProfilesSectionHidden(prProfilesExist && !company.page_config?.show_prs && company.displayType !== "pr_firm");
  }, [company, setPrProfilesSectionHidden, prProfilesExist]);

  // Keeps the PR agencies section hidden boolean updated
  useEffect(() => {
    // As above, only show in "Add a section" list if there actually are PR agencies.
    setPrAgenciesSectionHidden(
      prAgenciesExist && !company.page_config?.show_pr_agencies && company.displayType !== "pr_firm",
    );
  }, [company, setPrAgenciesSectionHidden, prAgenciesExist]);

  // Values and functions made available to consumers of the company context
  // (Memoized based on state values to avoid triggering unnecessary component rerenders)
  const providerValue = useMemo(
    () => ({
      // alphabetized
      aboutSectionHidden,
      asyncError,
      company,
      companyId,
      doTopLevelFetch,
      generalError,
      hashtagsSectionHidden,
      isUpdating,
      openEditModal,
      prAgenciesExist,
      prAgenciesSectionHidden,
      prProfilesExist,
      prProfilesSectionHidden,
      reloadCompany,
      setAsyncError,
      setCompany,
      setCompanyId,
      setDoTopLevelFetch,
      setGeneralError,
      setIsUpdating,
      setOpenEditModal,
      setPrAgenciesExist,
      setPrProfilesExist,
      setTopLevelIsLoading,
      topLevelIsLoading,
      viewerContext,
      // NB: must stay in lockstep with the actionable items in AddSectionButton. `hashtagsSectionHidden`
      // is intentionally NOT a term here — there is no hashtags menu item, so including it would render
      // an "Add a section" button that opens an empty dropdown for no-hashtag companies.
      hiddenSectionsAvailable:
        // alphabetized
        (company.displayType === "pr_firm" && !company.page_config?.show_brands) ||
        (company.displayType === "pr_firm" && !company.page_config?.show_prs) ||
        aboutSectionHidden ||
        prAgenciesSectionHidden ||
        prProfilesSectionHidden,
    }),
    [
      companyId,
      company,
      viewerContext,
      aboutSectionHidden,
      hashtagsSectionHidden,
      prProfilesSectionHidden,
      prAgenciesSectionHidden,
      prProfilesExist,
      prAgenciesExist,
      reloadCompany,
      topLevelIsLoading,
      doTopLevelFetch,
      openEditModal,
      isUpdating,
      generalError,
      asyncError,
    ],
  );

  return <CompanyContext.Provider value={providerValue}>{children}</CompanyContext.Provider>;
}

export { CompanyContext, CompanyContextProvider };

CompanyContextProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};
